import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";
import Logger from "../../../services/Logger";
import StatsWordWheelService from "../../../services/StatsWordWheelService";
import { CsvDownloadButton, csvNumber, downloadCsv } from "../../pzl_common/stats/adminCsvExport";
import { WORD_WHEEL_DEFAULT_SEASON, WORD_WHEEL_SEASON_OPTIONS } from "../wordWheelLevelUtils";

const RANGE_OPTIONS = [
    { value: "all", label: "All" },
    /** Rolling 7 days ending now (plays from one week ago through today). */
    { value: "week", label: "Week", days: 7 },
    { value: "1m", label: "Last month", days: 30 },
    { value: "3m", label: "Last 3 months", days: 90 },
    { value: "12m", label: "Last 12 months", days: 365 },
];

const DEFAULT_RANGE = "week";

const RANGE_STORAGE_KEY = "admin.wordwheel.userStats.range";

const readStoredRange = () => {
    try {
        const stored = window.localStorage.getItem(RANGE_STORAGE_KEY);
        if (stored === "7d") return "week";
        return RANGE_OPTIONS.some((option) => option.value === stored) ? stored : DEFAULT_RANGE;
    } catch {
        return DEFAULT_RANGE;
    }
};

const writeStoredRange = (value) => {
    try {
        window.localStorage.setItem(RANGE_STORAGE_KEY, value);
    } catch {
        /* private mode or storage disabled — keep the in-memory selection */
    }
};

const rangeDays = (value) => RANGE_OPTIONS.find((option) => option.value === value)?.days ?? null;

/** Backend treats omitted/0 start as last month — send an early bound for All. */
function rangeBounds(range) {
    const end = dayjs();
    if (range === "all") {
        return { start: dayjs("2000-01-01").startOf("day"), end };
    }
    const days = rangeDays(range);
    if (days == null) {
        return { start: end.subtract(7, "day").startOf("day"), end };
    }
    return { start: end.subtract(days, "day").startOf("day"), end };
}

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

const formatDecimal = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(1) : "—");

const formatPercent = (value) => `${formatDecimal(value)}%`;

const formatDuration = (seconds) => {
    const totalSeconds = Number(seconds);
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "—";
    if (totalSeconds < 60) return `${Math.round(totalSeconds)} sec`;
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = Math.round(totalSeconds % 60);
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
};

const formatDateTime = (epochMillis) =>
    epochMillis ? dayjs(Number(epochMillis)).format("YYYY-MM-DD HH:mm") : "—";

export default function WordWheelUserStatsPanel({ language }) {
    const [range, setRange] = useState(readStoredRange);
    const [season, setSeason] = useState(WORD_WHEEL_DEFAULT_SEASON);
    const [excludeTest, setExcludeTest] = useState(true);
    const [excludeStaff, setExcludeStaff] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);

    const handleRangeChange = useCallback((value) => {
        setRange(value);
        writeStoredRange(value);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const { start, end } = rangeBounds(range);
                const result = await StatsWordWheelService.userPlayStats({
                    start: start.unix(),
                    end: end.unix(),
                    language,
                    season,
                    includeTest: !excludeTest,
                    excludeStaff,
                });
                if (!cancelled) {
                    setStats(result ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load word wheel user stats:", err);
                if (!cancelled) {
                    setError("Failed to load user statistics.");
                    setStats(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [range, season, language, excludeTest, excludeStaff]);

    const totals = stats?.totals ?? {};
    const journey = stats?.journey ?? {};

    const daily = useMemo(() => (Array.isArray(stats?.daily) ? stats.daily : []), [stats]);
    const levelBands = useMemo(() => (Array.isArray(stats?.byLevelBand) ? stats.byLevelBand : []), [stats]);
    const progressionActors = useMemo(
        () => (Array.isArray(stats?.progression?.actors) ? stats.progression.actors : []),
        [stats]
    );
    const topLevels = useMemo(() => {
        const rows = Array.isArray(stats?.topLevels) ? [...stats.topLevels] : [];
        return rows.sort((a, b) => (Number(a.level) || 0) - (Number(b.level) || 0));
    }, [stats]);
    const journeyBands = useMemo(() => (Array.isArray(journey?.byBand) ? journey.byBand : []), [journey]);
    const furthestPlayers = useMemo(
        () => (Array.isArray(journey?.furthestPlayers) ? journey.furthestPlayers : []),
        [journey]
    );

    const dailyLabels = useMemo(() => daily.map((row) => dayjs(row.date).format("MMM D")), [daily]);
    const progressionTimes = useMemo(() => {
        const times = new Set();
        progressionActors.forEach((actor) => {
            (Array.isArray(actor.points) ? actor.points : []).forEach((point) => {
                const time = Number(point.time);
                if (Number.isFinite(time)) times.add(time);
            });
        });
        return [...times].sort((a, b) => a - b);
    }, [progressionActors]);
    const progressionSeries = useMemo(
        () =>
            progressionActors.map((actor) => {
                const levelsByTime = new Map(
                    (Array.isArray(actor.points) ? actor.points : []).map((point) => [
                        Number(point.time),
                        Number(point.level),
                    ])
                );
                return {
                    id: actor.id,
                    label: actor.label,
                    data: progressionTimes.map((time) => levelsByTime.get(time) ?? null),
                    connectNulls: true,
                    showMark: true,
                };
            }),
        [progressionActors, progressionTimes]
    );

    const hasPlays = Number(totals.plays ?? 0) > 0;

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="ww-user-stats-range-label">Duration</InputLabel>
                    <Select
                        labelId="ww-user-stats-range-label"
                        label="Duration"
                        value={range}
                        onChange={(event) => handleRangeChange(event.target.value)}
                    >
                        {RANGE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeTest}
                            onChange={(event) => setExcludeTest(event.target.checked)}
                        />
                    }
                    label="Exclude test accounts"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeStaff}
                            onChange={(event) => setExcludeStaff(event.target.checked)}
                        />
                    }
                    label="Exclude staff"
                />

                <Typography variant="body2" color="text.secondary">
                    {language} · signed-in players and guest sessions
                </Typography>
            </Box>

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && stats && (
                <>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                            gap: 1,
                            mb: 3,
                        }}
                    >
                        <SummaryTile label="Signed-in players" value={formatNumber(totals.players)} hint="Distinct accounts" />
                        <SummaryTile
                            label="Android"
                            value={formatNumber(totals.playersAndroid)}
                            hint="Signed-in · Word Wheel Quest"
                        />
                        <SummaryTile
                            label="Apple"
                            value={formatNumber(totals.playersApple)}
                            hint="Signed-in · Word Wheel Quest"
                        />
                        <SummaryTile
                            label="Other / web"
                            value={formatNumber(totals.playersOther)}
                            hint="No Android or Apple device"
                        />
                        <SummaryTile label="Guests" value={formatNumber(totals.guests)} hint="Distinct sessions, not signed in" />
                        <SummaryTile
                            label="Finished a puzzle"
                            value={`${formatNumber(totals.playersCompleted)} / ${formatNumber(totals.guestsCompleted)}`}
                            hint="Players / guests with a completion"
                        />
                        <SummaryTile label="Plays" value={formatNumber(totals.plays)} hint={`${formatNumber(totals.guestPlays)} by guests`} />
                        <SummaryTile label="Completed plays" value={formatNumber(totals.completedPlays)} hint={formatPercent(totals.completionRate)} />
                        <SummaryTile label="Plays per player" value={formatDecimal(totals.playsPerPlayer)} hint="Players and guests" />
                        <SummaryTile
                            label="Average solve time"
                            value={formatDuration(totals.averageDurationSeconds)}
                            hint={`${formatNumber(totals.timedCompletedPlays)} timed completions`}
                        />
                        <SummaryTile
                            label="Longest solve time"
                            value={formatDuration(totals.maxDurationSeconds)}
                            hint="Completed puzzles"
                        />
                    </Box>

                    {!hasPlays && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            No plays recorded in this period for {language}
                            {excludeTest ? " (test accounts excluded)" : ""}
                            {excludeStaff ? " (staff excluded)" : ""}.
                        </Alert>
                    )}

                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                        Players per day
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Distinct signed-in players split by platform (Android / Apple from Word Wheel
                                        Quest push tokens, falling back to signup source). Other is web or unknown.
                                        Guests have no platform. The notification series marks Word Wheel Quest pushes
                                        sent that day (admin broadcast and puzzle publish).
                                    </Typography>
                                </Box>
                                <CsvDownloadButton
                                    label="Download Players per day CSV"
                                    disabled={daily.length === 0}
                                    onClick={() => downloadCsv(
                                        "word-wheel-players-per-day.csv",
                                        [
                                            "date",
                                            "players",
                                            "playersAndroid",
                                            "playersApple",
                                            "playersOther",
                                            "guests",
                                            "plays",
                                            "guestPlays",
                                            "completedPlays",
                                            "notifications",
                                        ],
                                        daily.map((row) => [
                                            row.date ?? "",
                                            csvNumber(row.players),
                                            csvNumber(row.playersAndroid),
                                            csvNumber(row.playersApple),
                                            csvNumber(row.playersOther),
                                            csvNumber(row.guests),
                                            csvNumber(row.plays),
                                            csvNumber(row.guestPlays),
                                            csvNumber(row.completedPlays),
                                            csvNumber(row.notifications),
                                        ])
                                    )}
                                />
                            </Box>
                            {daily.length > 0 ? (
                                <LineChart
                                    height={300}
                                    xAxis={[{ scaleType: "point", data: dailyLabels }]}
                                    yAxis={[
                                        { id: "activity", min: 0 },
                                        { id: "notifications", min: 0, position: "right" },
                                    ]}
                                    series={[
                                        {
                                            data: daily.map((row) => Number(row.playersAndroid) || 0),
                                            label: "Android",
                                            color: "#3DDC84",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => Number(row.playersApple) || 0),
                                            label: "Apple",
                                            color: "#555555",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => Number(row.playersOther) || 0),
                                            label: "Other signed-in",
                                            color: "#3BA666",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => Number(row.guests) || 0),
                                            label: "Guests",
                                            color: "#f0a202",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => Number(row.plays) || 0),
                                            label: "Plays",
                                            color: "#1976d2",
                                            showMark: daily.length <= 40,
                                            yAxisId: "activity",
                                        },
                                        {
                                            data: daily.map((row) => {
                                                const count = Number(row.notifications) || 0;
                                                return count > 0 ? count : null;
                                            }),
                                            label: "Notifications sent",
                                            color: "#c62828",
                                            showMark: true,
                                            connectNulls: false,
                                            yAxisId: "notifications",
                                        },
                                    ]}
                                />
                            ) : (
                                <Alert severity="info">No daily activity in this period.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                        Player level progression over time
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Each line is a signed-in player or guest session. The chart shows the 12 most active
                                        players in this period; X is puzzle start time and Y is puzzle level.
                                        {Number(stats?.progression?.otherActors) > 0
                                            ? ` ${formatNumber(stats.progression.otherActors)} other players are hidden for readability.`
                                            : ""}
                                    </Typography>
                                </Box>
                                <CsvDownloadButton
                                    label="Download player level progression CSV"
                                    disabled={progressionActors.length === 0}
                                    onClick={() => downloadCsv(
                                        "word-wheel-level-progression.csv",
                                        ["id", "label", "plays", "time", "level"],
                                        progressionActors.flatMap((actor) => {
                                            const points = Array.isArray(actor.points) ? actor.points : [];
                                            return points.map((point) => [
                                                actor.id ?? "",
                                                actor.label ?? "",
                                                csvNumber(actor.plays),
                                                csvNumber(point.time),
                                                csvNumber(point.level),
                                            ]);
                                        })
                                    )}
                                />
                            </Box>
                            {progressionTimes.length > 0 ? (
                                <LineChart
                                    height={420}
                                    xAxis={[
                                        {
                                            scaleType: "time",
                                            data: progressionTimes.map((time) => new Date(time)),
                                            valueFormatter: (value) => dayjs(value).format("MMM D, HH:mm"),
                                            label: "Puzzle start time",
                                        },
                                    ]}
                                    yAxis={[{ label: "Puzzle level", min: 1 }]}
                                    series={progressionSeries}
                                    grid={{ horizontal: true, vertical: true }}
                                />
                            ) : (
                                <Alert severity="info">No level progression data in this period.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                        Puzzle levels played
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Plays grouped into level bands of 100. &quot;No level&quot; means the puzzle has no season level yet.
                                    </Typography>
                                </Box>
                                <CsvDownloadButton
                                    label="Download puzzle levels played CSV"
                                    disabled={levelBands.length === 0}
                                    onClick={() => downloadCsv(
                                        "word-wheel-levels-played.csv",
                                        [
                                            "band",
                                            "levelFrom",
                                            "levelTo",
                                            "players",
                                            "guests",
                                            "plays",
                                            "guestPlays",
                                            "completedPlays",
                                            "playSharePercent",
                                        ],
                                        levelBands.map((row) => [
                                            row.band ?? "",
                                            csvNumber(row.levelFrom),
                                            csvNumber(row.levelTo),
                                            csvNumber(row.players),
                                            csvNumber(row.guests),
                                            csvNumber(row.plays),
                                            csvNumber(row.guestPlays),
                                            csvNumber(row.completedPlays),
                                            csvNumber(row.playSharePercent),
                                        ])
                                    )}
                                />
                            </Box>
                            {levelBands.length > 0 ? (
                                <BarChart
                                    height={300}
                                    xAxis={[{ scaleType: "band", data: levelBands.map((row) => row.band) }]}
                                    series={[
                                        {
                                            data: levelBands.map((row) => Number(row.plays) || 0),
                                            label: "Plays",
                                            color: "#1976d2",
                                        },
                                        {
                                            data: levelBands.map((row) => Number(row.completedPlays) || 0),
                                            label: "Completed",
                                            color: "#3BA666",
                                        },
                                        {
                                            data: levelBands.map((row) => Number(row.players) || 0),
                                            label: "Signed-in players",
                                            color: "#7b1fa2",
                                        },
                                        {
                                            data: levelBands.map((row) => Number(row.guests) || 0),
                                            label: "Guests",
                                            color: "#f0a202",
                                        },
                                    ]}
                                />
                            ) : (
                                <Alert severity="info">No level data for the plays in this period.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    {topLevels.length > 0 && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                    Most played levels
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    The busiest levels of the period, listed in level order.
                                </Typography>
                                <TableContainer sx={{ overflowX: "auto" }}>
                                    <Table size="small" stickyHeader sx={{ minWidth: 420 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Plays</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Signed-in</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Guests</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Completed</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {topLevels.map((row) => (
                                                <TableRow key={`level-${row.level}`}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.level}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.plays)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.players)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.guests)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.completedPlays)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    flexWrap: "wrap",
                                    mb: 1,
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                    Journey level reached
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <FormControl size="small" sx={{ minWidth: 170 }}>
                                    <InputLabel id="ww-user-stats-season-label">Journey season</InputLabel>
                                    <Select
                                        labelId="ww-user-stats-season-label"
                                        label="Journey season"
                                        value={season}
                                        onChange={(event) => setSeason(event.target.value)}
                                    >
                                        {WORD_WHEEL_SEASON_OPTIONS.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <CsvDownloadButton
                                    label="Download journey level reached CSV"
                                    disabled={journeyBands.length === 0}
                                    onClick={() => downloadCsv(
                                        "word-wheel-journey-level-reached.csv",
                                        ["band", "levelFrom", "levelTo", "players"],
                                        journeyBands.map((row) => [
                                            row.band ?? "",
                                            csvNumber(row.levelFrom),
                                            csvNumber(row.levelTo),
                                            csvNumber(row.players),
                                        ])
                                    )}
                                />
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                How far players have progressed in the {journey.season ?? season} journey. Signed-in
                                players only — guests have no journey — and it covers every player with a journey row,
                                not only the selected period.
                            </Typography>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <SummaryTile label="Players on journey" value={formatNumber(journey.players)} />
                                <SummaryTile label="Lowest level" value={formatNumber(journey.levelMin)} />
                                <SummaryTile label="Highest level" value={formatNumber(journey.levelMax)} />
                                <SummaryTile label="Average level" value={formatDecimal(journey.levelAverage)} />
                            </Box>

                            {journeyBands.length > 0 ? (
                                <BarChart
                                    height={280}
                                    xAxis={[{ scaleType: "band", data: journeyBands.map((row) => row.band) }]}
                                    series={[
                                        {
                                            data: journeyBands.map((row) => Number(row.players) || 0),
                                            label: "Players",
                                            color: "#7b1fa2",
                                        },
                                    ]}
                                />
                            ) : (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    No journey progress recorded for {journey.season ?? season}.
                                </Alert>
                            )}

                            {furthestPlayers.length > 0 && (
                                <TableContainer sx={{ overflowX: "auto", mt: 2 }}>
                                    <Table size="small" stickyHeader sx={{ minWidth: 560 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Name / Email</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {furthestPlayers.map((player) => (
                                                <TableRow key={player.cloudUserId ?? `${player.email}-${player.level}`}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{formatNumber(player.level)}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" component="div">
                                                            {player.name || "—"}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" component="div">
                                                            {player.email || "—"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{formatDateTime(player.updatedAt)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
}

function SummaryTile({ label, value, hint }) {
    return (
        <Box sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}
